// MAJLIS-140 (D-59, P-39, EM-20f; PROPOSED 2026-09-29): a PASSIVE seat's voice track, on the phone.
//
// In a passive room the server publishes the seat's voiced translations as a LiveKit audio track, `voice-<seat id>`,
// so the browser's echo canceller has them as its far-end reference (the MP3 <audio> path was not cancelled on the
// owner's phones, and the phone transcribed its own translation again: a loop). The track plays through the SAME
// remote-voice element every other seat's voice uses (media/hearing.ts, D-22): livekitRoom.ts hands it to Hearing like
// any remote audio track. What this module decides, and nothing else:
//  - SUBSCRIBED only while the seat wants it (condition 3): the Auto-play switch ON, or a tapped clip playing. OFF, the
//    phone is unsubscribed - nothing plays, whatever the server sends;
//  - READY before a request goes out: when the track exists and the phone is not subscribed, it subscribes and waits
//    (VOICE_TRACK_SUBSCRIBE_MS) for LiveKit to say so, so a clip's first frames are not lost to the subscription. A
//    track that does not exist yet is ready at once: the server waits for the phone's first subscription itself;
//  - a subscription that fails, or never comes, is REPORTED (condition 5: the switch's line, and a gap record).
import type { Clock, TimerId } from "../clock";
import { VOICE_TRACK_SUBSCRIBE_MS } from "../constants";

export const VOICE_IDENTITY_PREFIX = "voice-";

/** Is this LiveKit participant a passive seat's voice track (never a seat)? */
export function isVoiceIdentity(identity: string): boolean {
  return identity.startsWith(VOICE_IDENTITY_PREFIX);
}

/** What a LiveKit connection reports about the voice track, and the one control it offers. */
export type VoiceTrackEvent = "published" | "subscribed" | "unsubscribed" | "failed" | "gone";
export interface VoiceTrackControl {
  setVoiceSubscribed(want: boolean): void;
}

export interface VoiceTrackLinkOptions {
  clock: Clock;
  /** The subscription failed (LiveKit said so) or never came: the app shows its line and reports it (P-39). */
  onFailed?: (reason: "subscribe") => void;
  log?: (message: string, body?: unknown) => void;
}

export class VoiceTrackLink {
  private state: "absent" | "published" | "subscribed" = "absent";
  private wanted = false;
  private control: VoiceTrackControl | null = null;
  private waiters: { done: (ok: boolean) => void; timer: TimerId }[] = [];

  constructor(private readonly opts: VoiceTrackLinkOptions) {}

  /** A LiveKit connection came up (or went: null). The voice track is re-learned from its own events. */
  attach(control: VoiceTrackControl | null): void {
    this.control = control;
    this.state = "absent";
    if (!control) this.settle(false);
  }

  /** From the current connection only (publish.ts filters stale ones). */
  event(e: VoiceTrackEvent): void {
    if (e === "published") {
      this.state = "published";
      this.control?.setVoiceSubscribed(this.wanted);
    } else if (e === "subscribed") {
      this.state = "subscribed";
      this.settle(true);
    } else if (e === "unsubscribed") {
      if (this.state === "subscribed") this.state = "published";
    } else if (e === "gone") {
      this.state = "absent";
    } else {
      this.opts.log?.("voice track: the subscription failed");
      this.settle(false, true);
    }
  }

  /** Condition 3: subscribed exactly while the seat wants it. */
  want(on: boolean): void {
    if (on === this.wanted) return;
    this.wanted = on;
    if (this.state !== "absent") this.control?.setVoiceSubscribed(on);
  }

  wants(): boolean {
    return this.wanted;
  }

  /** Resolves true once a clip asked for now will be heard from its first frame: subscribed, or no track yet (the
   * server waits for the first subscription). False - reported - when the subscription failed or did not come. */
  ready(): Promise<boolean> {
    if (this.state !== "published") return Promise.resolve(true);
    this.control?.setVoiceSubscribed(true);
    return new Promise((resolve) => {
      const waiter = {
        done: resolve,
        timer: this.opts.clock.setTimeout(() => {
          this.waiters = this.waiters.filter((w) => w !== waiter);
          this.opts.log?.("voice track: no subscription in time", { assumption_ms: VOICE_TRACK_SUBSCRIBE_MS });
          this.opts.onFailed?.("subscribe");
          resolve(false);
        }, VOICE_TRACK_SUBSCRIBE_MS),
      };
      this.waiters.push(waiter);
    });
  }

  private settle(ok: boolean, report = false): void {
    const waiters = this.waiters;
    this.waiters = [];
    for (const w of waiters) {
      this.opts.clock.clearTimeout(w.timer);
      w.done(ok);
    }
    if (report) this.opts.onFailed?.("subscribe");
  }
}
